/**
 * usePageMeta — updates document <title> and key <meta> tags per route.
 *
 * This is a lightweight alternative to react-helmet for SPA per-page SEO.
 * Google renders JavaScript and picks up these values for indexing.
 *
 * Usage:
 *   usePageMeta({
 *     title: "Explore Worlds — Adventure Venture",
 *     description: "Browse 77+ pre-built AI RPG worlds...",
 *     canonical: "https://adventureventure.app/explore",
 *   });
 */

import { useEffect } from "react";

interface PageMetaOptions {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  /** Set true for pages that should not be indexed (login, 404, etc.) */
  noindex?: boolean;
}

const DEFAULT_TITLE = "Adventure Venture — AI Dungeon Master with Persistent NPC Memory";
const DEFAULT_DESCRIPTION =
  "An AI game master that actually remembers your NPCs, holds grudges, and adapts to your choices. Build custom worlds, bring your own AI key, and adventure with friends.";

function setMeta(selector: string, attr: string, value: string) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export function usePageMeta({ title, description, canonical, ogImage, noindex }: PageMetaOptions) {
  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    setMeta('meta[name="description"]', "content", description);

    // Robots — noindex pages should never be surfaced in search results
    if (noindex) {
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
    }

    // Open Graph
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    if (canonical) setMeta('meta[property="og:url"]', "content", canonical);
    if (ogImage) setMeta('meta[property="og:image"]', "content", ogImage);

    // Twitter
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    if (ogImage) setMeta('meta[name="twitter:image"]', "content", ogImage);

    // Canonical
    if (canonical) {
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.setAttribute("href", canonical);
    }

    // Reset on unmount so navigating away doesn't leave stale meta
    return () => {
      document.title = DEFAULT_TITLE;
      setMeta('meta[name="description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[name="robots"]', "content", "index, follow");
      setMeta('meta[property="og:title"]', "content", DEFAULT_TITLE);
      setMeta('meta[property="og:description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:url"]', "content", "https://adventureventure.app/");
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.setAttribute("href", "https://adventureventure.app/");
    };
  }, [title, description, canonical, ogImage, noindex]);
}
